import Fade from '@mui/material/Fade';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { getRoleTransition, getRoleTransitionText } from '../../functions';
const useStyles = makeStyles()(() => ({
    overlay: {
        alignItems: 'center',
        backdropFilter: 'blur(3px)',
        backgroundColor: 'rgba(12, 19, 31, 0.22)',
        display: 'flex',
        inset: 0,
        justifyContent: 'center',
        padding: 24,
        position: 'fixed',
        zIndex: 1000001
    },
    content: {
        alignItems: 'center',
        animation: `${keyframes `
            from { transform: translateY(4px); }
            to { transform: translateY(0); }
        `} 300ms cubic-bezier(0.2, 0, 0, 1)`,
        backgroundColor: 'rgba(24, 34, 49, 0.92)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 14,
        boxShadow: '0 8px 28px rgba(0, 0, 0, 0.12)',
        color: 'rgba(255, 255, 255, 0.92)',
        display: 'flex',
        gap: 12,
        maxWidth: 360,
        padding: '16px 20px',
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none'
        }
    },
    indicator: {
        animation: `${keyframes `
            0%, 100% { opacity: 0.35; }
            50% { opacity: 0.85; }
        `} 1600ms ease-in-out infinite`,
        backgroundColor: '#b8cbea',
        borderRadius: '50%',
        flexShrink: 0,
        height: 6,
        width: 6,
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 0.6
        }
    },
    message: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '20px',
        overflowWrap: 'anywhere'
    }
}));
/**
 * A quiet status overlay for promotions and demotions. Keeps the meeting
 * visible, blocks clicks during reconnection, and fades away once joined.
 *
 * @returns {ReactElement|null}
 */
export default function RoleTransitionOverlay() {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const transition = useSelector(getRoleTransition);
    const [lastTransition, setLastTransition] = useState(transition);
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const conferenceEnded = useSelector((state) => state['features/base/conference'].conferenceEnded);
    // Keep the last message mounted throughout the exit fade.
    useEffect(() => {
        if (transition) {
            setLastTransition(transition);
        }
    }, [transition]);
    if (conferenceEnded || typeof document === 'undefined') {
        return null;
    }
    const host = document.getElementById('modalReact') || document.body;
    return createPortal(React.createElement(Fade, { easing: 'cubic-bezier(0.2, 0, 0, 1)', in: Boolean(transition), mountOnEnter: true, timeout: reduceMotion ? 0 : { enter: 300, exit: 220 }, unmountOnExit: true },
        React.createElement("div", { className: classes.overlay },
            React.createElement("div", { "aria-atomic": 'true', "aria-live": 'polite', className: classes.content, role: 'status' },
                React.createElement("span", { "aria-hidden": 'true', className: classes.indicator }),
                React.createElement("span", { className: classes.message }, getRoleTransitionText(transition || lastTransition, t))))), host);
}
